// Outsourced-vendor charge capture — GET vendors + POST charge at dispatch time.
//
// IPD:        /ipd_admission/{admission_id}/billing/vendor-charge-capture/
// EMERGENCY:  /emergency/visits/{visit_id}/billing/vendor-charge-capture/
//
// get_patient_lab_tests/ returns both admission_id and visit_id on every row;
// the unused key is "". Pick by patient_type — never mix the two.
import axios from "axios";
import { getAuthHeaders } from "../../ipd-billing/constants";
import {
  openEmergencyBilling,
  reverseEmergencyCharges,
} from "../../emergency/api/emergencyBillingApi";
import { cancelCharge } from "../../ipd-billing/billing-api";

const apiRoot = () => process.env.REACT_APP_BACKEND_ROUTE_URL;

/** Blank / missing id from the lab list ("", null, undefined). */
const blankId = (raw) => raw == null || raw === "" || raw === "-";

/**
 * medical_service_id — pass through as returned (number or uuid string).
 * Empty / snomed-… / deleted-service markers cannot be billed.
 */
export function validMedicalServiceId(order) {
  const candidates = [
    order?.medicalServiceId,
    order?.medical_service_id,
    order?.emr_medical_service_id,
    order?.medicalServiceNo,
    order?.medical_service_no,
    order?.cpt,
    order?.service_id,
  ];
  for (const raw of candidates) {
    if (blankId(raw)) continue;
    const s = String(raw).trim();
    if (!s || /^snomed-/i.test(s)) continue;
    const n = Number(s);
    if (Number.isFinite(n) && String(n) === s) return n;
    return s;
  }
  return null;
}

/** Non-blank visit_id from a mapped patient row or raw API item. */
export function resolveEmergencyVisitId(source) {
  if (!source) return null;
  const raw = source.visitId ?? source.visit_id;
  if (blankId(raw)) return null;
  return String(raw).trim();
}

/** Non-blank admission_id from a mapped patient row or raw API item. */
export function resolveAdmissionId(source) {
  if (!source) return null;
  const raw = source.admissionId ?? source.admission_id;
  if (blankId(raw)) return null;
  return String(raw).trim();
}

/**
 * Billing scope for vendor APIs — mirrors patient_type on the lab row.
 * @returns {{ type: 'ipd', admissionId: string } | { type: 'emergency', visitId: string } | null}
 */
export function resolveBillingScope(patient) {
  if (!patient) return null;
  const type = String(patient.patientType || "").toUpperCase();

  if (type === "EMERGENCY") {
    const visitId = resolveEmergencyVisitId(patient);
    // Blank visit_id → hide billing; visit could not be resolved.
    if (!visitId) return null;
    return { type: "emergency", visitId };
  }

  if (
    type === "IPD" ||
    (patient.admissionId != null && patient.admissionId !== "")
  ) {
    const admissionId = resolveAdmissionId(patient);
    if (!admissionId) return null;
    return { type: "ipd", admissionId };
  }

  return null;
}

/** Row with a billing scope + billable service ids — eligible for vendor lookup. */
export function canShowVendorFlow(orders, patient) {
  if (!resolveBillingScope(patient)) return false;
  return (orders || []).some((o) => validMedicalServiceId(o) != null);
}

/** Cache key for async vendor availability on one sample/study row's orders. */
export function vendorLookupKey(patient, orders) {
  const scope = resolveBillingScope(patient);
  if (!scope) return null;
  const scopeKey =
    scope.type === "ipd" ? `adm:${scope.admissionId}` : `er:${scope.visitId}`;
  const nos = (orders || [])
    .map((o) => String(o?.orderNo || "").trim())
    .filter(Boolean)
    .sort()
    .join("|");
  return nos ? `${scopeKey}|${nos}` : null;
}

const vendorCaptureUrl = (scope) => {
  if (scope?.type === "emergency") {
    return `${apiRoot()}emergency/visits/${scope.visitId}/billing/vendor-charge-capture/`;
  }
  return `${apiRoot()}ipd_admission/${scope.admissionId}/billing/vendor-charge-capture/`;
};

const ensureEmergencyBillingOpen = async (visitId) => {
  try {
    await openEmergencyBilling(visitId);
  } catch (err) {
    // Idempotent on the server; surface only when capture would fail anyway.
    console.warn("Emergency billing-setup:", err?.message || err);
  }
};

/**
 * Emergency capture nests fields under data; IPD may be flat.
 * Prefer data.* when present so charge_event.id is always reachable.
 */
const unwrapVendorBody = (body) => {
  if (body?.status === "Failed") {
    throw new Error(body?.message || "Vendor charge request failed");
  }
  const nested =
    body?.data && typeof body.data === "object" && !Array.isArray(body.data)
      ? body.data
      : null;
  return nested ? { ...body, ...nested } : body;
};

/** Prefer data.charge_event.id (Emergency); fall back to flat IPD shape. */
const chargeEventIdFromResult = (result) =>
  result?.data?.charge_event?.id ||
  result?.charge_event?.id ||
  result?.charge_event_id ||
  null;

export async function fetchVendorChargeOptions(scopeOrAdmissionId, medicalServiceId) {
  const scope =
    scopeOrAdmissionId && typeof scopeOrAdmissionId === "object"
      ? scopeOrAdmissionId
      : { type: "ipd", admissionId: String(scopeOrAdmissionId) };
  // GET vendor-charge-capture also requires an open billing account on ER.
  if (scope.type === "emergency") {
    await ensureEmergencyBillingOpen(scope.visitId);
  }
  const res = await axios.get(vendorCaptureUrl(scope), {
    headers: getAuthHeaders(true),
    params: { medical_service_id: medicalServiceId },
    skipGlobalLoader: true,
  });
  return unwrapVendorBody(res.data);
}

export async function captureVendorCharge(scopeOrAdmissionId, payload) {
  const scope =
    scopeOrAdmissionId && typeof scopeOrAdmissionId === "object"
      ? scopeOrAdmissionId
      : { type: "ipd", admissionId: String(scopeOrAdmissionId) };
  if (scope.type === "emergency") {
    await ensureEmergencyBillingOpen(scope.visitId);
  }
  const res = await axios.post(vendorCaptureUrl(scope), payload, {
    headers: getAuthHeaders(true),
  });
  return unwrapVendorBody(res.data);
}

/**
 * Vendors that can run every outsourceable test on the sample, with rates summed.
 * Tests whose vendor list is empty are in-house only and do not constrain the pick.
 */
export async function fetchCommonVendorsForOrders(scopeOrAdmissionId, orders) {
  const scope =
    scopeOrAdmissionId && typeof scopeOrAdmissionId === "object"
      ? scopeOrAdmissionId
      : { type: "ipd", admissionId: String(scopeOrAdmissionId) };

  // Open ER billing once per modal load before any vendor GETs (idempotent).
  if (scope.type === "emergency") {
    await ensureEmergencyBillingOpen(scope.visitId);
  }

  const scoped = (orders || [])
    .map((order) => ({ order, medicalServiceId: validMedicalServiceId(order) }))
    .filter((row) => row.medicalServiceId != null);

  if (scoped.length === 0) {
    return { vendors: [], tests: [], canOutsource: false };
  }

  const tests = await Promise.all(
    scoped.map(async ({ order, medicalServiceId }) => {
      const data = await fetchVendorChargeOptions(scope, medicalServiceId);
      return {
        order,
        medicalServiceId,
        serviceName: data.service_name || order.service,
        vendors: Array.isArray(data.vendors) ? data.vendors : [],
      };
    }),
  );

  const outsourceable = tests.filter((t) => t.vendors.length > 0);
  if (outsourceable.length === 0) {
    return { vendors: [], tests, canOutsource: false };
  }

  const buildVendorRows = (vendorList) =>
    vendorList.map((v, index) => {
      const picked = outsourceable.map(
        (t) => t.vendors.find((row) => row.vendor_id === v.vendor_id) || {},
      );
      const vendorRate = picked.reduce(
        (sum, row) => sum + (Number(row.rate) || 0),
        0,
      );
      return {
        vendor_id: v.vendor_id,
        vendor_name: v.vendor_name,
        address: v.address || "",
        vendor_rate: vendorRate,
        is_default: index === 0,
        test_count: picked.filter((row) => row.vendor_id != null).length,
      };
    });

  // One outsourceable test on the sample — show its vendors directly.
  if (outsourceable.length === 1) {
    const vendors = buildVendorRows(outsourceable[0].vendors);
    return { vendors, tests, canOutsource: vendors.length > 0 };
  }

  const [first, ...rest] = outsourceable;
  const common = first.vendors.filter((v) =>
    rest.every((t) => t.vendors.some((row) => row.vendor_id === v.vendor_id)),
  );

  if (common.length > 0) {
    const vendors = buildVendorRows(common);
    return { vendors, tests, canOutsource: vendors.length > 0 };
  }

  // No vendor covers every outsourced test — still list vendors per test so
  // CBC (or any single outsourceable line) is not hidden behind in-house only.
  const vendorMap = new Map();
  outsourceable.forEach((t) => {
    t.vendors.forEach((v) => {
      if (!vendorMap.has(v.vendor_id)) vendorMap.set(v.vendor_id, v);
    });
  });
  const vendors = buildVendorRows(Array.from(vendorMap.values()));
  return {
    vendors,
    tests,
    canOutsource: vendors.length > 0,
    partialCoverage: true,
  };
}

/** POST one or many vendor charges; returns charge_event.id keyed by orderNo. */
export async function captureVendorChargesForOrders(
  scopeOrAdmissionId,
  orders,
  vendorId,
  { dispatchRef = "" } = {},
) {
  const scope =
    scopeOrAdmissionId && typeof scopeOrAdmissionId === "object"
      ? scopeOrAdmissionId
      : { type: "ipd", admissionId: String(scopeOrAdmissionId) };

  const chargeDate = new Date().toISOString().split("T")[0];
  const remarks = String(dispatchRef || "").trim() || undefined;
  const rows = (orders || [])
    .map((order) => ({
      order,
      medical_service_id: validMedicalServiceId(order),
    }))
    .filter((row) => row.medical_service_id != null);

  if (rows.length === 0) {
    return { chargeEventsByOrder: {}, captured: 0 };
  }

  const chargeEventsByOrder = {};

  if (rows.length === 1) {
    const { order, medical_service_id } = rows[0];
    const result = await captureVendorCharge(scope, {
      vendor_id: vendorId,
      medical_service_id,
      quantity: 1,
      charge_date: chargeDate,
      remarks,
    });
    const eventId = chargeEventIdFromResult(result);
    if (eventId) chargeEventsByOrder[order.orderNo] = eventId;
    return { chargeEventsByOrder, captured: eventId ? 1 : 0 };
  }

  const payload = {
    charges: rows.map(({ medical_service_id }) => ({
      vendor_id: vendorId,
      medical_service_id,
      quantity: 1,
      charge_date: chargeDate,
      remarks,
    })),
  };
  const bulk = await captureVendorCharge(scope, payload);
  const results = Array.isArray(bulk?.results) ? bulk.results : [];
  results.forEach((entry, index) => {
    const order = rows[index]?.order;
    if (!order || !entry?.ok) return;
    // Bulk entry.result may itself nest under data for Emergency.
    const eventId = chargeEventIdFromResult(entry?.result);
    if (eventId) chargeEventsByOrder[order.orderNo] = eventId;
  });
  const captured = Object.keys(chargeEventsByOrder).length;
  return { chargeEventsByOrder, captured, bulk };
}

/**
 * Soft-cancel after a failed status post.
 * Emergency → charge-reverse; IPD → charges/{id}/cancel.
 */
export async function reverseVendorCharges(scope, chargeEventIds, reason) {
  const ids = (chargeEventIds || []).filter(Boolean);
  if (!scope || ids.length === 0) return;

  if (scope.type === "emergency") {
    await reverseEmergencyCharges(scope.visitId, ids, reason);
    return;
  }

  await Promise.allSettled(
    ids.map((id) => cancelCharge(scope.admissionId, id, reason)),
  );
}
